import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { t } from '../i18n'

/**
 * In-app replacements for confirm() and prompt(): the Discord Activity runs
 * in a frame without allow-modals, where the browser's dialogs never show
 * (confirm returns false, prompt null) — so nothing asked that way could
 * ever be confirmed there.
 */

interface Request {
  title: string
  text?: string
  /** A text field with this value; without it the dialog only confirms. */
  value?: string
  readOnly?: boolean
  maxLength?: number
  confirmLabel?: string
  /** Hides the cancel button (a message to acknowledge). */
  noCancel?: boolean
  danger?: boolean
  resolve: (value: string | null) => void
}

let current: Request | null = null
const listeners = new Set<() => void>()

function show(req: Omit<Request, 'resolve'>): Promise<string | null> {
  // A newer question replaces one left unanswered.
  current?.resolve(null)
  return new Promise((resolve) => {
    current = { ...req, resolve }
    listeners.forEach((l) => l())
  })
}

function close(value: string | null) {
  const req = current
  current = null
  listeners.forEach((l) => l())
  req?.resolve(value)
}

/** Asks to confirm an action; resolves true when confirmed. */
export async function confirmDialog(opts: { title: string; text?: string; confirmLabel?: string; danger?: boolean }): Promise<boolean> {
  return (await show(opts)) !== null
}

/** Asks for a line of text; resolves null when cancelled. */
export function promptDialog(opts: { title: string; text?: string; value?: string; maxLength?: number; confirmLabel?: string }): Promise<string | null> {
  return show({ ...opts, value: opts.value ?? '' })
}

/** Shows a value to copy by hand, e.g. a link when copying failed. */
export async function showValueDialog(opts: { title: string; text?: string; value: string }): Promise<void> {
  await show({ ...opts, readOnly: true, noCancel: true, confirmLabel: t('dialog.done') })
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}
const getCurrent = () => current

/** Renders the open dialog; mounted once at the root of the app. */
export function DialogHost() {
  const req = useSyncExternalStore(subscribe, getCurrent)
  if (!req) return null
  // In full screen only the fullscreen element is visible.
  return createPortal(<DialogView key={req.title + (req.value ?? '')} req={req} />, document.fullscreenElement ?? document.body)
}

function DialogView({ req }: { req: Request }) {
  const [value, setValue] = useState(req.value ?? '')
  const input = useRef<HTMLInputElement>(null)
  const confirmBtn = useRef<HTMLButtonElement>(null)
  const withField = req.value !== undefined

  useEffect(() => {
    if (withField) {
      input.current?.focus()
      input.current?.select()
    } else {
      confirmBtn.current?.focus()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Before the player's hotkeys: Escape must not leave theater mode.
      e.preventDefault()
      e.stopImmediatePropagation()
      close(null)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [withField])

  const submit = () => {
    if (withField && !req.readOnly && !value.trim()) return
    close(withField ? value : '')
  }

  return (
    <div className="dialog" onPointerDown={(e) => e.target === e.currentTarget && close(null)}>
      <form
        className="dialog__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        // Keys typed here are not player hotkeys.
        onKeyDown={(e) => e.stopPropagation()}
      >
        <h2 id="dialog-title" className="dialog__title">
          {req.title}
        </h2>
        {req.text && <p className="dialog__text">{req.text}</p>}
        {withField && (
          <input
            ref={input}
            className="dialog__input"
            value={value}
            readOnly={req.readOnly}
            maxLength={req.maxLength}
            onChange={(e) => setValue(e.target.value)}
            onFocus={(e) => req.readOnly && e.currentTarget.select()}
            aria-label={req.title}
          />
        )}
        <div className="dialog__actions">
          {!req.noCancel && (
            <button type="button" className="btn" onClick={() => close(null)}>
              {t('dialog.cancel')}
            </button>
          )}
          <button
            ref={confirmBtn}
            type="submit"
            className={`btn ${req.danger ? 'btn--danger' : 'btn--primary'}`}
            disabled={withField && !req.readOnly && !value.trim()}
          >
            {req.confirmLabel ?? t('dialog.ok')}
          </button>
        </div>
      </form>
    </div>
  )
}
